import React, { useEffect, useState, useCallback } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ReactFlow, Background, Controls, MiniMap, BackgroundVariant, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { api } from '../api';
import { PortalWorkflowDetailOut, PortalWorkflowExecutionOut } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';
import type { TranslationKey } from '../i18n';
import { useBeheerder } from '../hooks/useBeheerder';
import { useTabParam } from '../hooks/useTabParam';
import AccessStatusChip from '../components/AccessStatusChip';
import CapabilityPill from '../components/CapabilityPill';
import { McpIcon } from '../components/icons';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { alpha } from '@mui/material/styles';
import WorkflowToolsTab from '../components/WorkflowToolsTab';
import ServerUsersTab from '../components/ServerUsersTab';
import WorkflowConnectTabContent from '../components/WorkflowConnectTabContent';
import WorkflowNodeDetail from '../components/WorkflowNodeDetail';
import { workflowDiagramNodeTypes } from '../components/WorkflowDiagramNode';
import { config } from '../config';
import { useWorkflowUsers } from '../hooks/useWorkflowUsers';
import Collapse from '@mui/material/Collapse';
import LinkIcon from '@mui/icons-material/Link';
import WebhookIcon from '@mui/icons-material/Webhook';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

const TAB_ABOUT = 'about';
const TAB_TOOLS = 'tools';
const TAB_DIAGRAM = 'diagram';
const TAB_EXECUTIONS = 'executions';
const TAB_WEBHOOK = 'webhook';
const TAB_CONNECT = 'connect';
const TAB_USERS = 'users';
const WORKFLOW_TABS = [
  TAB_ABOUT,
  TAB_CONNECT,
  TAB_TOOLS,
  TAB_DIAGRAM,
  TAB_EXECUTIONS,
  TAB_WEBHOOK,
  TAB_USERS,
] as const;

const EXECUTION_STATUS_COLORS: Record<string, string> = {
  completed: '#16a34a',
  failed: '#dc2626',
  running: '#2563eb',
  pending: '#9ca3af',
};

const translateDynamic = (t: ReturnType<typeof useTranslation>, prefix: string, value: string): string => {
  const key = `${prefix}.${value}` as TranslationKey;
  const translated = t(key);
  return translated !== key ? translated : value;
};

interface WebhookTabProps {
  workflow: PortalWorkflowDetailOut;
}

function CopyableBlock({ value, mono, t }: { value: string; mono?: boolean; t: ReturnType<typeof useTranslation> }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '8px',
        p: 2,
        position: 'relative',
        fontFamily: mono ? '"SF Mono", "Fira Code", "Consolas", monospace' : 'inherit',
        fontSize: '0.8rem',
        lineHeight: 1.7,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        color: '#334155',
      }}
    >
      {value}
      <Tooltip title={copied ? t('common.copied') : t('common.copy')}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function WebhookTab({ workflow }: WebhookTabProps) {
  const t = useTranslation();

  if (!workflow.link_id) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <WebhookIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography sx={{ color: 'text.secondary' }}>{t('workflow.webhook.noLink')}</Typography>
      </Card>
    );
  }

  const baseUrl = window.location.origin.replace('portal-', '');
  const webhookUrl = `${baseUrl}/api/v1/workflow_execution/trigger/${workflow.link_id}`;
  const variables = (workflow.variable_definitions || []) as Array<{
    name: string;
    type?: string;
    description?: string;
    required?: boolean;
  }>;

  let triggerParams = variables;
  if (triggerParams.length === 0) {
    const triggerNode = (workflow.nodes || []).find(
      (n: Record<string, unknown>) =>
        ((n.data as Record<string, unknown>)?.type as string) === 'trigger',
    );
    const triggerData = (triggerNode?.data as Record<string, unknown>) || {};
    // Collect parameters referenced in other nodes via {{ trigger.xxx }}.
    const triggerRefs = new Set<string>();
    const triggerPattern = /\{\{\s*trigger\.(\w+)\s*\}\}/g;
    for (const node of workflow.nodes || []) {
      const nodeStr = JSON.stringify(node);
      let m: RegExpExecArray | null;
      while ((m = triggerPattern.exec(nodeStr)) !== null) {
        triggerRefs.add(m[1]);
      }
      triggerPattern.lastIndex = 0;
    }
    if (triggerRefs.size > 0) {
      triggerParams = Array.from(triggerRefs).map((name) => ({
        name,
        type: 'string',
        description: '',
        required: false,
      }));
    }
  }

  const exampleBody: Record<string, string> = {};
  for (const p of triggerParams) {
    exampleBody[p.name] = `<${p.name}>`;
  }
  const curlExample = `curl -X POST '${webhookUrl}' \\
  -H 'Authorization: Bearer <token>' \\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify({ trigger_input: exampleBody }, null, 2)}'`;

  return (
    <>
      <Card sx={{ p: 3, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>
          {t('workflow.webhook.url')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2 }}>
          {t('workflow.webhook.description')}
        </Typography>
        <CopyableBlock value={webhookUrl} mono t={t} />
      </Card>
      <Card sx={{ p: 3, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>
          {t('workflow.webhook.auth')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
          {t('workflow.webhook.authDescription')}
        </Typography>
      </Card>
      {triggerParams.length > 0 && (
        <Card sx={{ p: 3, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>
            {t('workflow.webhook.parameters')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2 }}>
            {t('workflow.webhook.parametersDescription')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('workflow.webhook.name')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('workflow.webhook.type')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }} />
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('workflow.webhook.descriptionCol')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {triggerParams.map((p) => (
                  <TableRow key={p.name}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>
                      {p.name}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Chip label={p.type || 'string'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.required ? t('workflow.webhook.required') : t('workflow.webhook.optional')}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: p.required ? 'rgba(220,38,38,0.08)' : 'rgba(0,0,0,0.04)',
                          color: p.required ? '#dc2626' : 'text.secondary',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {p.description || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
      <Card sx={{ p: 3 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
          {t('workflow.webhook.example')}
        </Typography>
        <CopyableBlock value={curlExample} mono t={t} />
      </Card>
    </>
  );
}

function WorkflowDetailContent() {
  const { name } = useParams<{ name: string }>();
  const { getAccessTokenSilently } = useOidcContext();
  const navigate = useNavigate();
  const t = useTranslation();
  const isBeheerder = useBeheerder();
  const [workflow, setWorkflow] = useState<PortalWorkflowDetailOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useTabParam(TAB_ABOUT, WORKFLOW_TABS);
  const [selectedNode, setSelectedNode] = useState<Record<string, unknown> | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [executions, setExecutions] = useState<PortalWorkflowExecutionOut[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [executionsLoaded, setExecutionsLoaded] = useState(false);
  const { workflowUsers, usersLoading, usersLoaded, loadWorkflowUsers, togglingUser, handleToggleUserAccess } =
    useWorkflowUsers(name, isBeheerder);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [expandedExecution, setExpandedExecution] = useState<string | null>(null);

  const loadExecutions = useCallback(async () => {
    if (!name) return;
    setExecutionsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await api.getWorkflowExecutions(token, name);
      setExecutions(data);
      setExecutionsLoaded(true);
    } catch {
      setExecutions([]);
    } finally {
      setExecutionsLoading(false);
    }
  }, [name, getAccessTokenSilently]);

  useEffect(() => {
    if (tab === TAB_EXECUTIONS && !executionsLoaded && !executionsLoading) {
      void loadExecutions();
    }
  }, [tab, executionsLoaded, executionsLoading, loadExecutions]);

  useEffect(() => {
    if (tab === TAB_USERS && !usersLoaded && !usersLoading && isBeheerder) {
      void loadWorkflowUsers();
    }
  }, [tab, usersLoaded, usersLoading, isBeheerder, loadWorkflowUsers]);

  const loadWorkflow = useCallback(async () => {
    if (!name) return;
    try {
      const token = await getAccessTokenSilently();
      const data = await api.getWorkflowDetail(token, name);
      setWorkflow(data);
    } catch {
      navigate('/workflows', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [name, getAccessTokenSilently, navigate]);

  useEffect(() => {
    void loadWorkflow();
  }, [loadWorkflow]);

  const handleRemoveWorkflow = useCallback(async () => {
    if (!workflow) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.removeWorkflow(token, workflow.name);
      navigate('/workflows');
    } catch (e: unknown) {
      setRemoveError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setRemoving(false);
    }
  }, [workflow, getAccessTokenSilently, navigate, t]);

  const handleTrigger = useCallback(async () => {
    if (!workflow) return;
    setTriggering(true);
    setTriggerSuccess(false);
    try {
      const token = await getAccessTokenSilently();
      await api.triggerWorkflow(token, workflow.name);
      setTriggerSuccess(true);
      setTimeout(() => setTriggerSuccess(false), 3000);
      setExecutionsLoaded(false); // Force reload on next tab switch
      if (tab === TAB_EXECUTIONS) void loadExecutions();
    } catch (e: unknown) {
      setRemoveError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setTriggering(false);
    }
  }, [workflow, getAccessTokenSilently, tab, loadExecutions, t]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={140} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Box sx={{ textAlign: 'center', py: 12 }}>
        <AccountTreeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }} />
        <Typography sx={{ fontWeight: 600, color: 'text.secondary', mb: 2 }}>{t('workflow.notFound.title')}</Typography>
        <Button onClick={() => navigate('/workflows')} variant="outlined" size="small">{t('common.backToOverview')}</Button>
      </Box>
    );
  }

  const triggerNodes = (workflow.nodes || []).filter(
    (n: Record<string, unknown>) => ((n.data as Record<string, unknown>)?.type as string) === 'trigger',
  );
  const triggerTypes = Array.from(
    new Set(
      triggerNodes.map(
        (n: Record<string, unknown>) => ((n.data as Record<string, unknown>)?.triggerType as string) || 'unknown',
      ),
    ),
  );
  const isManualTrigger = triggerTypes.includes('manual');
  const isWebhookTrigger = triggerTypes.includes('webhook');
  const isMcpTrigger = triggerTypes.includes('mcp');
  const nodeCount = (workflow.nodes || []).length;

  // Tabs that are actually rendered for this workflow + role. A `?tab=` value
  // that points at a hidden tab (e.g. a shared `?tab=webhook` link opened by a
  // non-beheerder) falls back to About so the indicator stays in sync.
  const isActive = workflow.access_status === 'active';
  const visibleTabs: string[] = [
    TAB_ABOUT,
    ...(isMcpTrigger && isActive ? [TAB_CONNECT] : []),
    ...(isMcpTrigger ? [TAB_TOOLS] : []),
    TAB_DIAGRAM,
    TAB_EXECUTIONS,
    ...(isWebhookTrigger && isBeheerder && isActive ? [TAB_WEBHOOK] : []),
    ...(isBeheerder ? [TAB_USERS] : []),
  ];
  const selectedTab = visibleTabs.includes(tab) ? tab : TAB_ABOUT;

  // Variables the workflow author references via {{ variables.X }} but for
  // which this org has no value set yet. Surface as a warning on the active
  // link so beheerders can fix it before the next execution silently uses an
  // empty value.
  const declaredVariables = (workflow.variable_definitions ?? []) as Array<{ name?: string }>;
  const variableValues = (workflow.variable_values ?? {}) as Record<string, unknown>;
  const missingVariableNames = declaredVariables
    .map((v) => v.name)
    .filter((name): name is string => {
      if (!name) return false;
      const value = variableValues[name];
      return value === undefined || value === null || value === '';
    });
  const showMissingVariablesWarning
    = workflow.access_status === 'active' && missingVariableNames.length > 0;

  // Pass node.data through so the custom node can pick icon/colour by type, mirroring the dashboard editor.
  const nodes = (workflow.nodes || []).map((n: Record<string, unknown>) => {
    const data = (n.data as Record<string, unknown>) || {};
    return {
      id: n.id as string,
      type: 'workflowNode',
      position: (n.position as { x: number; y: number }) || { x: 0, y: 0 },
      data: {
        label: (data.label as string) || (n.id as string),
        type: data.type as string | undefined,
        triggerType: data.triggerType as string | undefined,
        toolName: data.toolName as string | undefined,
        parserId: data.parserId as string | undefined,
        mcpToolName: data.mcpToolName as string | undefined,
      },
    };
  });

  const edges = (workflow.edges || []).map((e: Record<string, unknown>) => ({
    id: e.id as string,
    source: e.source as string,
    target: e.target as string,
    sourceHandle: (e.sourceHandle as string) || undefined,
    targetHandle: (e.targetHandle as string) || undefined,
    animated: true,
    style: { stroke: 'rgba(0,0,0,0.15)', strokeWidth: 1.5 },
  }));

  const hasVisualizations = workflow?.has_visualizations ?? false;
  const visualizationNodes = workflow?.visualization_nodes ?? [];

  const copyVisualizationLink = (nodeId: string) => {
    const url = `${window.location.origin}/visualizations/${workflow?.name}/${nodeId}`;
    void navigator.clipboard.writeText(url);
    setCopiedLink(nodeId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
        onClick={() => navigate('/workflows')}
        sx={{ mb: 3, color: 'text.secondary', fontSize: '0.85rem', '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' } }}
      >
        {t('workflow.backButton')}
      </Button>
      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 4, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '14px',
                bgcolor: workflow.access_status === 'active' ? 'primary.main' : 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AccountTreeIcon
                sx={{
                  fontSize: 26,
                  color: workflow.access_status === 'active' ? '#fff' : '#71717a',
                }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ mb: 0.25 }}>
                {workflow.display_name}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                {workflow.description || t('common.noDescription')}
              </Typography>
              {triggerTypes.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {triggerTypes.map((tt) => {
                    const label = translateDynamic(t, 'workflow.triggerType', tt);
                    if (tt === 'mcp') {
                      return (
                        <CapabilityPill
                          key={tt}
                          label={label}
                          color="primary.main"
                          bg={(theme) => alpha(theme.palette.primary.main, 0.1)}
                          icon={<McpIcon sx={{ fontSize: 13, color: 'primary.main' }} />}
                        />
                      );
                    }
                    const icon
                      = tt === 'cron' ? <ScheduleIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        : tt === 'webhook' ? <WebhookIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                          : tt === 'manual' ? <PlayArrowIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            : undefined;
                    return <CapabilityPill key={tt} label={label} icon={icon} />;
                  })}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <AccessStatusChip status={workflow.access_status} size="medium" />
              {isManualTrigger && workflow.access_status === 'active' && workflow.can_trigger && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  disabled={triggering}
                  onClick={() => void handleTrigger()}
                  sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'text.primary' } }}
                >
                  {t('workflow.trigger')}
                </Button>
              )}
              {workflow.access_status === 'active' && isBeheerder && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={removing}
                  onClick={() => setRemoveDialogOpen(true)}
                >
                  {removing ? t('workflow.removing') : t('workflow.remove')}
                </Button>
              )}
            </Box>
          </Box>
          {triggerSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('workflow.triggerSuccess')}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
              {t('workflow.stats.nodes', { count: nodeCount })}
            </Typography>
            {triggerTypes.length > 0 && (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                {t('workflow.stats.trigger', { type: triggerTypes.map((tt) => translateDynamic(t, 'workflow.triggerType', tt)).join(', ') })}
              </Typography>
            )}
          </Box>
        </Box>

        {showMissingVariablesWarning && (
          <Alert
            severity="warning"
            sx={{
              mx: 4,
              mb: 2,
              borderRadius: '10px',
              alignItems: 'flex-start',
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
              {t('workflow.variables.missingTitle')}
            </Typography>
            <Typography sx={{ fontSize: '0.825rem', color: 'text.secondary', lineHeight: 1.5 }}>
              {t('workflow.variables.missingBody')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {missingVariableNames.map((name) => (
                <Box
                  key={name}
                  component="code"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    bgcolor: 'rgba(0,0,0,0.05)',
                    fontFamily: '"SF Mono", "Fira Code", ui-monospace, monospace',
                    fontSize: 12,
                  }}
                >
                  {name}
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        <Tabs
          value={selectedTab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 4,
            borderTop: '1px solid rgba(0,0,0,0.05)',
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: '2px 2px 0 0',
              bgcolor: 'primary.main',
            },
          }}
        >
          <Tab label={t('workflow.tabs.about')} value={TAB_ABOUT} />
          {/* Connect sits between About and Tools. */}
          {isMcpTrigger && workflow.access_status === 'active' && (
            <Tab label={t('workflow.tabs.connect')} value={TAB_CONNECT} />
          )}
          {/* Tools lists the MCP tools this workflow exposes, like an MCP server's Tools tab. */}
          {isMcpTrigger && (
            <Tab label={t('workflow.tabs.tools')} value={TAB_TOOLS} />
          )}
          <Tab label={t('workflow.tabs.diagram')} value={TAB_DIAGRAM} />
          <Tab label={t('workflow.tabs.executions')} value={TAB_EXECUTIONS} />
          {isWebhookTrigger && isBeheerder && workflow.access_status === 'active' && (
            <Tab label={t('workflow.tabs.webhook')} value={TAB_WEBHOOK} />
          )}
          {isBeheerder && (
            <Tab label={t('workflow.tabs.users')} value={TAB_USERS} />
          )}
        </Tabs>
      </Card>
      {tab === TAB_ABOUT && (
        <>
          <Card sx={{ p: 4 }}>
            {workflow.about ? (
              <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: 'text.primary' }}>
                {workflow.about}
              </Typography>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  {t('workflow.about.empty')}
                </Typography>
              </Box>
            )}
          </Card>
          {hasVisualizations && visualizationNodes.length > 0 && (
            <Card sx={{ mt: 3, p: 3, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <LinkIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  {t('workflow.visualizations.title')}
                </Typography>
              </Box>
              {visualizationNodes.map((viz) => (
                <Box
                  key={viz.node_id}
                  sx={{
                    'display': 'flex',
                    'alignItems': 'center',
                    'gap': 2,
                    'py': 1.5,
                    'px': 2,
                    'borderRadius': '8px',
                    'border': '1px solid rgba(0,0,0,0.06)',
                    'mb': 1,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                  }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {viz.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontFamily: 'monospace' }}>
                      {window.location.origin}/visualizations/{workflow?.name}/{viz.node_id}
                    </Typography>
                  </Box>
                  <Tooltip title={copiedLink === viz.node_id ? t('common.copied') : t('common.copy')}>
                    <IconButton size="small" onClick={() => copyVisualizationLink(viz.node_id)}>
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Card>
          )}
        </>
      )}
      {tab === TAB_TOOLS && isMcpTrigger && (
        <WorkflowToolsTab
          nodes={(workflow.nodes || []) as Record<string, unknown>[]}
          workflowName={workflow.display_name}
        />
      )}
      {tab === TAB_DIAGRAM && (
        <Card sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              height: 500,
              bgcolor: '#fafafa',
            }}
          >
            {nodes.length > 0 ? (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={workflowDiagramNodeTypes}
                fitView
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable
                onNodeClick={(_event, node) => {
                  const fullNode = (workflow.nodes || []).find((n: Record<string, unknown>) => n.id === node.id);
                  setSelectedNode(fullNode || null);
                }}
                panOnDrag
                zoomOnScroll
                proOptions={{ hideAttribution: true }}
                defaultEdgeOptions={{ animated: true }}
              >
                <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="rgba(0,0,0,0.04)" />
                <Controls showInteractive={false} />
                <MiniMap pannable zoomable style={{ borderRadius: 8, overflow: 'hidden' }} />
              </ReactFlow>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">{t('workflow.empty')}</Typography>
              </Box>
            )}
          </Box>
        </Card>
      )}
      {tab === TAB_EXECUTIONS && (
        <Card sx={{ overflow: 'hidden' }}>
          {executionsLoading ? (
            <Box sx={{ p: 4 }}>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="text" height={40} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : executions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                {t('workflow.executions.empty')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 32, p: 0 }} />
                    <TableCell>{t('workflow.executions.status')}</TableCell>
                    <TableCell>{t('workflow.executions.trigger')}</TableCell>
                    <TableCell>{t('workflow.executions.startedBy')}</TableCell>
                    <TableCell>{t('workflow.executions.started')}</TableCell>
                    <TableCell>{t('workflow.executions.duration')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.map((ex) => {
                    const duration = ex.started_at && ex.finished_at
                      ? (() => {
                          const ms = new Date(ex.finished_at).getTime() - new Date(ex.started_at).getTime();
                          if (ms < 1000) return `${ms}ms`;
                          const s = Math.floor(ms / 1000);
                          if (s < 60) return `${s}s`;
                          return `${Math.floor(s / 60)}m ${s % 60}s`;
                        })()
                      : ex.status === 'running' ? '...' : '-';
                    const hasTriggerInput = ex.trigger_input && Object.keys(ex.trigger_input).length > 0;
                    const isExpanded = expandedExecution === ex.id;
                    return (
                      <React.Fragment key={ex.id}>
                        <TableRow
                          hover={hasTriggerInput}
                          onClick={() => hasTriggerInput && setExpandedExecution(isExpanded ? null : ex.id)}
                          sx={{ cursor: hasTriggerInput ? 'pointer' : 'default' }}
                        >
                          <TableCell sx={{ width: 32, p: 0, pl: 1 }}>
                            {hasTriggerInput && (
                              isExpanded
                                ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                : <KeyboardArrowRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={translateDynamic(t, 'workflow.executionStatus', ex.status)}
                              size="small"
                              sx={{
                                bgcolor: `${EXECUTION_STATUS_COLORS[ex.status] || '#9ca3af'}18`,
                                color: EXECUTION_STATUS_COLORS[ex.status] || '#9ca3af',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 22,
                              }}
                            />
                            {ex.error && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5 }}>
                                {ex.error}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={translateDynamic(t, 'workflow.triggerType', ex.trigger_type)}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{ex.triggered_by || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {ex.started_at ? new Date(ex.started_at).toLocaleString() : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>{duration}</TableCell>
                        </TableRow>
                        {hasTriggerInput && (
                          <TableRow key={`${ex.id}-detail`}>
                            <TableCell colSpan={6} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                              <Collapse in={isExpanded}>
                                <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc' }}>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                    Trigger input
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                                      fontSize: '0.75rem',
                                      lineHeight: 1.6,
                                      color: '#334155',
                                      m: 0,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {JSON.stringify(ex.trigger_input, null, 2)}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}
      {tab === TAB_WEBHOOK && isWebhookTrigger && isBeheerder && (
        <WebhookTab workflow={workflow} />
      )}
      {tab === TAB_CONNECT && isMcpTrigger && workflow.access_status === 'active' && (
        <WorkflowConnectTabContent
          workflow={workflow}
          workflowMcpUrl={`${config.api.mcpBaseUrl}/api/v1/mcp/workflow/${workflow.name}`}
          examplePrompt={
            workflow.about
            || (workflow.variable_definitions?.[0]?.name
              ? `Roep de ${workflow.display_name} workflow aan met ${workflow.variable_definitions[0].name} = "..."`
              : `Roep de ${workflow.display_name} workflow aan.`)
          }
          copied={false}
          onCopy={(text) => { void navigator.clipboard.writeText(text); }}
          isBeheerder={isBeheerder}
          t={t}
        />
      )}
      {tab === TAB_USERS && isBeheerder && (
        <ServerUsersTab
          users={workflowUsers}
          loading={usersLoading}
          togglingUser={togglingUser}
          onToggleAccess={handleToggleUserAccess}
        />
      )}
      <WorkflowNodeDetail
        node={selectedNode}
        onClose={() => setSelectedNode(null)}
        accessStatus={workflow.access_status}
        canTrigger={workflow.can_trigger ?? false}
        workflowName={workflow.name}
      />
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('workflow.remove')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('workflow.removeConfirm')}
          </Typography>
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }}>{removeError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveDialogOpen(false)} color="inherit">
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void handleRemoveWorkflow()} color="error" variant="contained" disabled={removing}>
            {removing ? t('workflow.removing') : t('workflow.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function WorkflowDetailPage() {
  return (
    <ReactFlowProvider>
      <WorkflowDetailContent />
    </ReactFlowProvider>
  );
}
